import * as React from "react"
import { Tab, Tabs, Row, Col, Modal } from "react-bootstrap"
import PoolGridRow from "../../../api/Randoms/RandomsModel/PoolGridRow"
import PoolStatistics from "./PoolStatistics"
import PoolGeneralInfo from "./PoolGeneralInfo"
import ImportPoolMembers from "./ImportPoolMembers"
import RunRandom from "./RunRandom"
import CloseProgramInterval from "./CloseProgramInterval"
import PoolMembersGrid from "./PoolMembersGrid"
import { AppState } from "../../../AppState"
import { connect } from "react-redux"
import { BuildUserAccess } from "../../../authentication/BuildUserAccess"
import { GetSelectedAssignedClinic } from "../../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import { isCompanyAdmin, isPortalAdmin } from "../../../authentication/isAdminRole"
import PoolData from "../../../api/Randoms/RandomsModel/PoolData"
import { Subscription } from "rxjs"
import PoolGeneralInfoGetObservable from "../../../api/Randoms/PoolGeneralInfoGet"
import { toast } from "react-toastify"
import PoolHistory from "./PoolHistory"
import PoolNotifications from "../Notification/PoolNotifications"

export interface PoolDetailsModalProps {
  details: PoolGridRow
  onHide: () => void
  isConsortium: boolean
}
export interface PoolDetailsModalAppStateProps {
  showRunRandom: boolean
  showPersonnelImport: boolean
  companyID: number
  isCompanyAdmin?: boolean
  isPortalAdmin?: boolean
}

interface PoolDetailsModalState {
  currentTab: string
  data?: PoolData
}

const StatisticsKey = "statistics"
const HistoryKey = "pool_history"
const GeneralInformationKey = "general_information"
const PoolMembersKey = "pool_members"
const RunRandomKey = "run_random"
const CloseProgramIntervalKey = "close_program_interval"
const NotificationKey = "notification"

export class PoolDetailsModal extends React.PureComponent<PoolDetailsModalProps & PoolDetailsModalAppStateProps, PoolDetailsModalState> {
  state: PoolDetailsModalState = {
    currentTab: StatisticsKey
  }

  subscriptions = new Subscription()

  componentDidMount() {
    this.loadPoolData()
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
  }

  loadPoolData = () => {
    this.subscriptions.add(
      PoolGeneralInfoGetObservable(this.props.details.PoolID).subscribe(
        (data: PoolData) => this.setState({ data: data }),
        (error: string) => toast.error(error)
      ))
  }

  handleRunFinish = () => this.setState({ currentTab: StatisticsKey })

  onPoolDetailsUpdate = () => {
    this.setState({ data: undefined })
    this.loadPoolData()
  }

  render() {
    const { details, onHide, isConsortium, isCompanyAdmin, isPortalAdmin } = this.props
    const limitedCons = isConsortium && isCompanyAdmin
    // TODO: Some permissions are missed in AppState:
    //          *userPermissions.CanRunRandoms
    //          *userPermissions.CanUpdatePoolMembership
    // const showRunRandom = userPermissions && (userPermissions.CanRunRandoms || userPermissions.CanManagePools)
    // const showPersonnelImport = userPermissions && (userPermissions.CanManagePersonnel || userPermissions.CanUpdatePoolMembership)

    return (
      <Modal backdrop="static" show={true} onHide={onHide} size="xl" dialogClassName="modal-90w">
        <Modal.Header closeButton>
          <Modal.Title>{isConsortium ? "CONSORTIUM DETAILS" : "POOL DETAILS"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="tab-container">
            <Row className="px-0 pt-2">
              <Col>
                <h4 className="px-4"><span className="text-uppercase">{details.Name}</span> ({details.ID})</h4>
                <Tabs defaultActiveKey={StatisticsKey} activeKey={this.state.currentTab} className="px-4 py-2" onSelect={(eventKey) => { eventKey && this.setState({ currentTab: eventKey }) }}>
                  <Tab eventKey={StatisticsKey} title="STATISTICS">
                    {StatisticsKey === this.state.currentTab && <PoolStatistics isConsortium={isConsortium} allowExcusedReport={isCompanyAdmin || isPortalAdmin || false} allowAuditReport={this.props.showRunRandom} currentCompany={this.props.companyID} poolId={details.PoolID} poolName={details.Name} />}
                  </Tab>
                  {!isConsortium && <Tab eventKey={HistoryKey} title="HISTORY">
                    {HistoryKey === this.state.currentTab && <PoolHistory isConsortium={isConsortium} allowExcusedReport={isCompanyAdmin || isPortalAdmin || false} currentCompany={this.props.companyID} poolId={details.PoolID} poolName={details.Name} />}
                  </Tab>}
                  {!limitedCons && <Tab eventKey={GeneralInformationKey} title="GENERAL INFORMATION">
                    {GeneralInformationKey === this.state.currentTab && !!this.state.data && <PoolGeneralInfo onPoolUpdate={this.onPoolDetailsUpdate} isConsortium={isConsortium} poolId={details.PoolID} poolData={this.state.data} isPortalAdmin={isPortalAdmin} />}
                  </Tab>}
                  <Tab eventKey={PoolMembersKey} title={isConsortium ? "CONSORTIUM MEMBERS" : "POOL MEMBERS"}>
                    {PoolMembersKey === this.state.currentTab && <PoolMembersGrid onPoolUpdate={this.onPoolDetailsUpdate} isConsortium={isConsortium} isCompanyAdmin={isCompanyAdmin} poolID={details.PoolID} poolName={details.Name} regulated={details.Regulated} useFileImportForPoolMembership={this.state.data?.UseFileImportForPoolMembership || false} />}
                  </Tab>
                  {this.props.showRunRandom && !limitedCons && <Tab eventKey={RunRandomKey} title="RUN RANDOM">
                    {RunRandomKey === this.state.currentTab && <RunRandom poolId={details.PoolID} poolName={details.Name} isCompanyAdmin={isCompanyAdmin} isPortalAdmin={isPortalAdmin} onRunFinish={this.handleRunFinish} />}
                  </Tab>}
                  {this.props.showPersonnelImport && !limitedCons && <Tab eventKey="personnel_import" title="PERSONNEL IMPORT">
                    <ImportPoolMembers poolID={details.PoolID} poolPrimaryIDType={this.state.data?.PrimaryIDType} isConsortium={isConsortium} multiplePools={(this.props.isPortalAdmin && !isConsortium) || false} />
                  </Tab>}
                  {!limitedCons && <Tab eventKey={CloseProgramIntervalKey} title="CLOSE PROGRAM INTERVAL">
                    {CloseProgramIntervalKey === this.state.currentTab && !!this.state.data && <CloseProgramInterval poolId={details.PoolID} poolName={details.Name} poolData={this.state.data} onPoolUpdate={this.onPoolDetailsUpdate} />}
                  </Tab>}
                </Tabs>
              </Col>
            </Row>
          </div>
        </Modal.Body>
      </Modal>
    )
  }
}

function mapStateToProps(state: AppState): PoolDetailsModalAppStateProps {
  const userAccess = BuildUserAccess(state)
  return {
    showRunRandom: userAccess.canManagePools || userAccess.CanManageConsortiumPools,
    showPersonnelImport: userAccess.canManagePersonnel || userAccess.CanManageConsortiumPools,
    companyID: GetSelectedAssignedClinic(state)?.AccountID || 0,
    isCompanyAdmin: isCompanyAdmin(state),
    isPortalAdmin: isPortalAdmin(state)
  }
}
const ConnectedPoolDetailsModal = connect(mapStateToProps)(PoolDetailsModal)
export default ConnectedPoolDetailsModal
